import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Menu, X, Info, Scale, Users, Star, Quote } from "lucide-react";
import { BIZ, PRACTICE_AREAS, BIO, TEAM_NOTE, REPUTATION } from "./mekingData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import LawInquiryForm from "@/components/LawInquiryForm";

const INK = "#151515";
const CRIMSON = "#B91C1C";
const CREAM = "#F7F4EF";
const SLATE = "#57534E";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function MekingLawSite() {
  const [open, setOpen] = useState(false);
  const links = [["Practice", "#practice"], ["Atty. Meking", "#attorney"], ["Offices", "#offices"], ["Consultation", "#consultation"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Inter', sans-serif" }} data-testid="meking-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');`}</style>

      <div data-testid="law-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: CRIMSON }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 shadow-sm" style={{ background: INK }}>
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="meking-logo" className="flex items-center gap-2 text-white font-black tracking-tight uppercase text-sm sm:text-base">
            <Scale size={20} style={{ color: CRIMSON }} /> Meking Law Firm
          </a>
          <div className="hidden lg:flex items-center gap-7">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-white/70 hover:text-white transition-colors">{l}</a>)}
          </div>
          <a href={`tel:${BIZ.phone}`} data-testid="meking-header-cta" className="hidden sm:inline-flex rounded-md px-4 py-2 text-sm font-bold uppercase tracking-wide" style={{ background: CRIMSON, color: "white" }}>Call Now</a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="meking-menu-toggle" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: INK }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-white/80">{l}</a>)}</div>}
      </header>

      {/* HERO — magazine masthead: oversized type, photo as a cropped inset, not full-bleed */}
      <section className="py-14 sm:py-20" style={{ background: INK }} data-testid="meking-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.span {...fadeUp} className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: "#F87171" }}>Quezon City · Angeles City, Pampanga</motion.span>
          <div className="mt-4 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end">
            <motion.h1 {...fadeUp} className="text-5xl sm:text-7xl font-black text-white leading-[0.95] tracking-tight">{BIZ.tagline}</motion.h1>
            <motion.div {...fadeUp} className="rounded-lg overflow-hidden shadow-2xl aspect-[4/3]">
              <img src="/law/meking-law-firm-concept/consultation.jpg" alt="Atty. Hazel Meking in consultation" className="w-full h-full object-cover object-top" />
            </motion.div>
          </div>
          <motion.div {...fadeUp} className="mt-8 flex flex-wrap items-center gap-6">
            <p className="text-white/70 max-w-md text-sm">Civil, corporate, criminal, family, labor, property and tax representation from a QC-based practice with a Pampanga branch.</p>
            <a href="#consultation" data-testid="meking-hero-cta" className="rounded-md px-7 py-3.5 text-sm font-bold uppercase tracking-wide" style={{ background: CRIMSON, color: "white" }}>Request Consultation</a>
          </motion.div>
        </div>
      </section>

      {/* PRACTICE AREAS — dense tag cluster, not a card grid */}
      <section id="practice" className="py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-black mb-8">Practice Areas</motion.h2>
          <motion.div {...fadeUp} className="flex flex-wrap justify-center gap-3">
            {PRACTICE_AREAS.map((s) => (
              <span key={s.title} className="rounded-full px-5 py-2.5 text-sm font-bold" style={{ background: INK, color: "white" }}>{s.title}</span>
            ))}
          </motion.div>
          <motion.div {...fadeUp} className="mt-10 grid sm:grid-cols-2 gap-4 text-left">
            {PRACTICE_AREAS.map((s) => (
              <div key={s.title} className="flex items-baseline gap-2">
                <span className="font-black text-sm shrink-0" style={{ color: CRIMSON }}>{s.title}</span>
                <span className="text-xs" style={{ color: SLATE }}>— {s.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ATTORNEY — pull-quote led */}
      <section id="attorney" className="py-16" style={{ background: INK }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.span {...fadeUp} className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: "#F87171" }}>Meet the Attorney</motion.span>
          <div className="mt-4 grid md:grid-cols-[auto_1fr] gap-6 items-start">
            <Quote size={40} style={{ color: CRIMSON }} className="shrink-0" />
            <div>
              <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-black text-white leading-snug">Named in a Supreme Court case. Publicly challenged a DOJ circular.</motion.h2>
              <motion.p {...fadeUp} className="mt-2 text-sm font-semibold" style={{ color: "#F87171" }}>Atty. Hazel "Heyz" L. Meking — {BIO.role}</motion.p>
            </div>
          </div>
          <motion.p {...fadeUp} className="mt-6 text-white/75 text-sm leading-relaxed">{BIO.confirmed}</motion.p>
          <motion.p {...fadeUp} className="mt-4 text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>{BIO.selfDescribed}</motion.p>
        </div>
      </section>

      {/* OFFICES — two-office diptych, real photos of the QC storefront and interior */}
      <section id="offices" className="grid sm:grid-cols-2">
        <div className="relative p-10 sm:p-14 min-h-[280px] flex flex-col justify-end">
          <img src="/law/meking-law-firm-concept/storefront-night.jpg" alt="The Meking Law Firm storefront sign, Quezon City" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(185,28,28,0.92), rgba(185,28,28,0.55))" }} />
          <div className="relative">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Main Office</h3>
            <p className="mt-2 text-2xl font-black text-white">Quezon City</p>
            <p className="mt-4 flex items-start gap-3 text-sm text-white/85"><MapPin size={18} className="mt-0.5 shrink-0" />{BIZ.qcAddress}</p>
            <p className="mt-2 flex items-start gap-3 text-sm text-white/85"><Phone size={18} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="meking-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a></p>
          </div>
        </div>
        <div className="relative p-10 sm:p-14 min-h-[280px] flex flex-col justify-end">
          <img src="/law/meking-law-firm-concept/office-interior.jpg" alt="Inside the Meking Law Firm office" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(127,29,29,0.92), rgba(127,29,29,0.55))" }} />
          <div className="relative">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Branch</h3>
            <p className="mt-2 text-2xl font-black text-white">Angeles City, Pampanga</p>
            <p className="mt-4 text-sm text-white/85">{BIZ.pampangaAddress}. Call the main office to confirm current branch details.</p>
          </div>
        </div>
      </section>
      <section className="min-h-[260px]">
        <iframe title="Meking Law Firm QC office map" src={BIZ.mapEmbed} className="w-full h-full min-h-[260px]" style={{ border: 0, display: "block" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      </section>

      {/* TEAM — wide banner with overlay caption */}
      <section id="team" className="relative">
        <img src="/law/meking-law-firm-concept/team.jpg" alt="The Meking Law Firm team at the office" className="w-full h-[320px] sm:h-[420px] object-cover" />
        <div className="absolute inset-0 flex items-end" style={{ background: "linear-gradient(0deg, rgba(21,21,21,0.9), transparent 55%)" }}>
          <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-10 w-full">
            <span className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: "#F87171" }}>Our Team</span>
            <h2 className="mt-2 text-2xl font-black text-white flex items-center gap-2"><Users size={22} style={{ color: CRIMSON }} /> A Staffed, Working Office</h2>
            <p className="mt-2 text-sm text-white/80 max-w-xl">{TEAM_NOTE}</p>
          </div>
        </div>
      </section>

      {/* REPUTATION + CONSULTATION side by side */}
      <section id="consultation" className="py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.7fr_1fr] gap-14 items-start">
          <motion.div {...fadeUp} className="text-center lg:text-left">
            <div className="inline-flex items-center gap-1.5 mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} fill={CRIMSON} style={{ color: CRIMSON }} />)}
            </div>
            <p className="text-4xl font-black" style={{ color: INK }}>{REPUTATION.rating}★</p>
            <p className="mt-1 text-sm" style={{ color: SLATE }}>across {REPUTATION.reviewCountLabel}</p>
            <p className="mt-4 text-xs" style={{ color: SLATE }}>{REPUTATION.note}</p>
          </motion.div>
          <motion.div {...fadeUp}>
            <LawInquiryForm
              testidPrefix="meking"
              businessName={BIZ.name}
              accent={CRIMSON}
              accentText="#FFFFFF"
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
              messengerUrl={BIZ.facebook}
            />
          </motion.div>
        </div>
      </section>

      <footer className="py-8" style={{ background: INK }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <p className="font-black text-white uppercase">Meking Law Firm</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="meking-law-firm-concept" />
    </div>
  );
}
